import React from 'react';
import { useChatUi } from '../ChatContext';

const STEPS = [
  { key: 'search', stages: ['understanding', 'searching'], label: 'Searching' },
  { key: 'read', stages: ['reading', 'finalizing'], label: 'Reading' },
  { key: 'write', stages: ['generating'], label: 'Writing' },
];

// stages of a manual search; other stages (chatting, recalling, clarifying) are replies without one
const SEARCH_STAGES = new Set(STEPS.flatMap((s) => s.stages));

function stepIndex(stage, hasText) {
  if (hasText || stage === 'generating') return 2;
  const i = STEPS.findIndex((s) => s.stages.includes(stage));
  return i === -1 ? 0 : i;
}

/**
 * Progress while an answer is being prepared: dots + the server's localized label, and - only once the server
 * reports a manual search - a three-step stepper (Searching -> Reading N pages -> Writing). A greeting or a
 * memory question never shows "Searching"; before the first status it is just "Thinking...".
 * Keeps the `.thinking-indicator` class the batch script waits on. Rendered only while the message is busy.
 */
export default function StatusLine({ message, compact }) {
  const { t } = useChatUi();
  const { stage, stageLabel, stageDetail, sources, text } = message;
  const hasText = Boolean(text);
  const current = stepIndex(stage, hasText);
  const pages = (stageDetail && stageDetail.pages) || (sources && sources.length) || 0;
  const label = stageLabel || t('Thinking...');
  const searching = SEARCH_STAGES.has(stage) || Boolean(sources && sources.length);

  if (compact) {
    // after the answer text is complete (follow-ups being prepared)
    return (
      <div className="thinking-indicator cv-status cv-status--compact">
        <span className="thinking-dots" aria-hidden="true"><span /><span /><span /></span>
        <span className="thinking-text">{label}</span>
      </div>
    );
  }

  return (
    <div className="thinking-indicator cv-status">
      <span className="thinking-dots" aria-hidden="true"><span /><span /><span /></span>
      <span className="thinking-text">{label}</span>
      {searching && (
        <ol className="cv-steps" aria-hidden="true">
          {STEPS.map((s, i) => (
            <li key={s.key} className={i < current ? 'is-done' : i === current ? 'is-current' : ''}>
              {s.key === 'read' && pages ? `${t('Reading')} ${pages} ${t('pages')}` : t(s.label)}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
